import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { createSelector } from 'reselect'
import { checkoutAccommodationOfferView } from '../view/accommodation'
import { getFlightItemsView } from '../view/flights'
import getExperienceItemsView from '../view/itemViews/getExperienceItemsView'

type Dates = {
  startDate: string | undefined
  endDate: string | undefined
}

// In some cases we don't have start and end date for flights or experiences.
// For example, if the experience happens on a single day, it may only have a start date.
// Therefore, we need to filter to return only dates that are defined
function getValidDates(accommodationDates: Array<Dates>, flightDates: Array<Dates>, experienceDates: Array<Dates>) {
  // Validate dates
  const isInvalidExperienceDates = experienceDates.find((date) => !date.startDate || !date.endDate)
  const isInvalidFlightsDates = flightDates.find((date) => !date.startDate || !date.endDate)
  const isInvalidAccommodationDates = accommodationDates.find((date) => !date.startDate || !date.endDate)

  // For combinations of accommodation + flights or accommodation + experiences, if we have at least the
  // accommodation dates and the flights or experiences dates fall within this interval,
  // it's enough to determine the coverage dates
  if (isInvalidExperienceDates && isInvalidFlightsDates && isInvalidAccommodationDates) return undefined
  if ((isInvalidExperienceDates || isInvalidFlightsDates) && isInvalidAccommodationDates) return undefined

  const dates = [...accommodationDates, ...flightDates, ...experienceDates]

  const filteredDates = dates.map(date => {
    const filteredDate: Partial<Dates> = {}
    if (date.startDate) filteredDate.startDate = date.startDate
    if (date.endDate) filteredDate.endDate = date.endDate
    return filteredDate
  })

  if (filteredDates.every(date => Object.keys(date).length === 0)) {
    return undefined
  }

  return filteredDates
}

const getInsuranceDatesFromCart = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getExperienceItemsView(state),
  (accommodationViews, flightViews, experienceViews): { startDate: string, endDate: string } | undefined => {
    if (!accommodationViews.hasRequiredData || !flightViews.hasRequiredData || !experienceViews.hasRequiredData) {
      return undefined
    }

    const accommodationDates = accommodationViews.data.map(item => ({
      startDate: item.startDate,
      endDate: item.endDate,
    }))

    const flights = flightViews.data.map(flightView => [
      ...(flightView.flights[0]?.journeyFlight.flights || []),
      ...(flightView.flights[1]?.journeyFlight.flights || []),
    ]).flat()

    const flightDates = flights.map(flight => ({
      startDate: flight.departingDate,
      endDate: flight.arrivalDate,
    }))

    const experienceDates = experienceViews.data.map(experience => ({
      startDate: experience.bookingDate ?? experience.ticketSummaryViews[0]?.date,
      endDate: moment(experience.bookingDate ?? experience.ticketSummaryViews[0]?.date).add(1, 'day').format(ISO_DATE_FORMAT),
    }))

    /**
     * Typically, insurance quotes are generated as soon as customers land on the first checkout step.
     * However, in some cases, the dates are selected only during the checkout process, and initially, they may be undefined.
    */
    const dates = getValidDates(accommodationDates, flightDates, experienceDates)
    if (!dates) return undefined

    if (dates.length) {
      return {
        startDate: moment.min(dates.filter(date => !!date.startDate).map(date => moment(date.startDate, ISO_DATE_FORMAT))).format(ISO_DATE_FORMAT),
        endDate: moment.max(dates.filter(date => !!date.endDate).map(date => moment(date.endDate, ISO_DATE_FORMAT))).format(ISO_DATE_FORMAT),
      }
    }
  },
)

export default getInsuranceDatesFromCart
